import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, XCircle, Loader2, CheckCircle2, CalendarX } from "lucide-react";
import { api } from "@/lib/api";

export default function CancelBooking() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, booking: null, error: "" });
  const [cancelling, setCancelling] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${api}/bookings/cancel/${token}`)
      .then(r => setState({ loading: false, booking: r.data, error: "" }))
      .catch((e) => {
        const detail = e?.response?.data?.detail || "Booking not found.";
        setState({ loading: false, booking: null, error: detail });
      });
  }, [token]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${api}/bookings/cancel/${token}`);
      setDone(true);
      toast.success("Your booking has been cancelled.");
    } catch {
      toast.error("Could not cancel. Please call (301) 201-4263.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 py-16 relative">
      <Link data-testid="cancel-back-home" to="/"
            className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-[0.25em]">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <img src="/brand/gi-logo.jpg" alt="GI Detailing" style={{ mixBlendMode: 'lighten' }} className="h-16 w-auto" />
        </div>

        <div className="border border-white/10 bg-surface/70 p-8 md:p-10" data-testid="cancel-card">
          {state.loading && (
            <div className="flex items-center gap-3 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Looking up your booking...</div>
          )}

          {!state.loading && state.error && (
            <div className="text-center py-6">
              <CalendarX className="w-10 h-10 mx-auto text-red-400 mb-4" />
              <div className="font-serif text-2xl mb-2">Booking not found</div>
              <p className="text-zinc-400 text-sm">{state.error}</p>
              <p className="mt-6 text-zinc-500 text-sm">If you think this is a mistake, call us at <span className="text-white">(301) 201-4263</span>.</p>
            </div>
          )}

          {!state.loading && state.booking && !done && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Cancel booking</div>
              <h1 className="font-serif text-3xl md:text-4xl mb-6">Are you sure?</h1>
              <div className="border border-white/10 p-5 space-y-3 mb-6" data-testid="cancel-details">
                <Row label="Name" value={state.booking.full_name} />
                <Row label="When" value={state.booking.when_line} />
                <Row label="Vehicle" value={state.booking.vehicle} />
                <Row label="Package" value={state.booking.package_name} />
                <Row label="Status" value={
                  <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
                    state.booking.status === "confirmed" ? "border-emerald-500/40 text-emerald-300" :
                    state.booking.status === "cancelled" ? "border-red-500/40 text-red-300" :
                    "border-yellow-500/40 text-yellow-300"
                  }`}>{state.booking.status}</span>
                } />
              </div>

              {state.booking.status === "cancelled" ? (
                <div className="text-center text-zinc-400 py-4">
                  <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-400 mb-2" />
                  This booking is already cancelled.
                </div>
              ) : (
                <>
                  <p className="text-sm text-zinc-400 mb-5 font-light">
                    Cancelling will free the slot immediately. You can always book a new appointment from our site.
                  </p>
                  <div className="flex gap-3">
                    <button
                      data-testid="cancel-confirm-btn"
                      onClick={doCancel}
                      disabled={cancelling}
                      className="beam-btn flex-1 border border-red-500/50 text-red-300 hover:bg-red-500/10 font-mono text-xs uppercase tracking-[0.25em] py-3.5 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      Yes, cancel my booking
                    </button>
                    <Link to="/" data-testid="cancel-keep-btn"
                          className="beam-btn bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:bg-zinc-200 transition-colors flex items-center gap-2">
                      Keep it
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}

          {done && (
            <div className="text-center py-6" data-testid="cancel-success">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-4" />
              <div className="font-serif text-3xl mb-2">Cancelled</div>
              <p className="text-zinc-400 text-sm">Your slot is now free. We hope to see you next time.</p>
              <Link to="/" className="mt-6 inline-block beam-btn bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-6 py-3 hover:bg-zinc-200 transition-colors">
                Back to site
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-4 text-sm">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 w-24 flex-shrink-0">{label}</div>
      <div className="text-zinc-200">{value}</div>
    </div>
  );
}
